// src/pages/vendor/Settings/GeneralSettings.tsx

import React, { useState, useEffect } from 'react';
import { Store, Save, Building2, Mail, Phone, Globe, FileText, User, Clock } from 'lucide-react';
import StoreCard from '../../../components/vendor/StoreCard';
import StoreModal from '../../../components/vendor/StoreModal';
import { vendorAPI } from '../../../api/vendor';
import type { Store as StoreType, Vendor } from '../../../types';
import { formatVendorType } from '../../../utils/formatVendorType';
import VendorTypeChangeModal from '../../../components/vendor/VendorTypeChangeModal';
import toast from '../../../utils/toast';

interface GeneralSettingsProps {
    vendor: Vendor | null;
    onVendorUpdate: () => Promise<void>;
}

const GeneralSettings: React.FC<GeneralSettingsProps> = ({ vendor, onVendorUpdate }) => {
    const [stores, setStores] = useState<StoreType[]>([]);
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showStoreModal, setShowStoreModal] = useState(false);
    const [selectedStore, setSelectedStore] = useState<StoreType | null>(null);
    const [showVendorTypeModal, setShowVendorTypeModal] = useState(false);

    useEffect(() => {
        fetchStores();
    }, []);

    const fetchStores = async () => {
        setLoading(true);
        try {
            const data = await vendorAPI.getVendorStores();
            setStores(data);
        } catch (error) {
            console.error('Failed to fetch store:', error);
            // If 404, vendor has no store yet - that's fine
        } finally {
            setLoading(false);
        }
    };


    const handleVendorTypeRequest = async (data: { requested_type: string; reason: string }) => {
        setIsProcessing(true);
        try {
            await vendorAPI.requestVendorTypeChange(data);
            toast.success('Vendor type change request submitted');
            setShowVendorTypeModal(false);
            await onVendorUpdate();
        } catch (error) {
            toast.error('Failed to submit request');
            throw error;
        } finally {
            setIsProcessing(false);
        }
    };

    const handleStoreSave = async (storeData: any) => {
        setIsProcessing(true);
        try {
            const response = selectedStore
                ? await vendorAPI.updateStore(selectedStore.id, storeData)
                : await vendorAPI.createStore(storeData);

            if (selectedStore) {
                setStores(prev => prev.map(s => s.id === selectedStore.id ? response : s));
            } else {
                setStores(prev => [...prev, response]);
            }
            setShowStoreModal(false);
            setSelectedStore(null);
        } catch (error) {
            console.error('Failed to save store:', error);
            throw error;
        } finally {
            setIsProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-medium text-gray-900">General Information</h3>
                        <p className="text-sm text-gray-500">
                            Manage your basic business information
                        </p>
                    </div>

                    <button
                        onClick={() => {
                            if (vendor?.can_create_store) {
                                setShowStoreModal(true);
                            } else {
                                toast.warning(vendor?.store_creation_disabled_reason || 'You cannot create a store at this time');
                            }
                        }}
                        title={vendor?.can_create_store ? 'Create a new store' : (vendor?.store_creation_disabled_reason || 'Store creation is disabled')}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors ${vendor?.can_create_store
                            ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                            : 'bg-gray-200 text-gray-400'
                            }`}
                    >
                        <Store className="h-4 w-4" />
                        Create Store
                    </button>
                </div>

                {stores.length > 0 ? stores.map((store) => (
                    <StoreCard
                        key={store.id}
                        store={store}
                        onEdit={() => {
                            setSelectedStore(store);
                            setShowStoreModal(true);
                        }}
                        isProcessing={isProcessing}
                    />
                )) : (
                    <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                        <Store className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <h3 className="text-lg font-medium text-gray-900">No Store Created</h3>
                        <p className="text-sm text-gray-500 mt-1">Create your store to start selling</p>
                        <button
                            onClick={() => {
                                if (vendor?.can_create_store) {
                                    setShowStoreModal(true);
                                } else {
                                    toast.warning(vendor?.store_creation_disabled_reason || 'You cannot create a store at this time');
                                }
                            }}
                            className={`mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors ${vendor?.can_create_store
                                ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                                : 'bg-gray-200 text-gray-400'
                                }`}
                        >
                            Create Store
                        </button>
                    </div>
                )}
                {/* Vendor Type Info */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="flex items-center gap-2">
                                <Building2 className="h-5 w-5 text-gray-400" />
                                <span className="font-medium text-gray-900">
                                    Vendor Type: {formatVendorType(vendor?.vendor_type ?? '')}
                                </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                                {vendor?.vendor_type === 'individual'
                                    ? 'You can only have one store with this account type.'
                                    : 'You can create multiple stores for your business.'
                                }
                            </p>
                        </div>

                        {/* ✅ Pending Request State */}
                        {vendor?.has_pending_vendor_request ? (
                            <span className="flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg text-sm font-medium">
                                <Clock className="h-4 w-4 animate-pulse" />
                                Request Pending
                            </span>
                        ) : vendor?.vendor_type === 'individual' ? (
                            <button
                                onClick={() => setShowVendorTypeModal(true)}
                                className="px-4 py-2 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            >
                                Request Vendor Type Change
                            </button>
                        ) : null}
                    </div>

                    {/* ✅ Show pending message */}
                    {vendor?.has_pending_vendor_request && (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-yellow-700">
                                Your vendor type change request is being reviewed. You'll receive an update via email once processed.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Store Modal */}
            {showStoreModal && (
                <StoreModal
                    isOpen={showStoreModal}
                    onClose={() => {
                        setShowStoreModal(false)
                        setSelectedStore(null)
                    }}
                    onSubmit={handleStoreSave}
                    initialData={selectedStore || undefined}
                    isProcessing={isProcessing}
                    vendorName={vendor?.user?.full_name || ''}
                    vendorType={vendor?.vendor_type || 'individual'}
                />
            )}
            {showVendorTypeModal && (
                <VendorTypeChangeModal
                    isOpen={showVendorTypeModal}
                    onClose={() => setShowVendorTypeModal(false)}
                    onSubmit={handleVendorTypeRequest}
                    isProcessing={isProcessing}
                />
            )}

        </div>
    );
};

export default GeneralSettings;